import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { RotateCw } from "lucide-react";
import type { BulkJobView } from "@amber/operations/contracts";
import {
  Button,
  Callout,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  FieldLabel,
  LinkButton,
  RadioCards,
  ReasonDialog,
  ReasonField,
  Select,
  cx,
  isApiError,
  useReason,
  useToast,
  useCommandMutation,
  useCsrf,
  formatCost,
} from "@amber/ui";
import {
  REPROCESS_STAGES,
  type ItemDetail,
  type ItemRow,
  type ItemsFilter,
  type RecoveryPreview,
} from "../../contracts/index";
import {
  jobsQuery,
  recoveryPreviewQuery,
  AFTER_COLLECTION_CHANGE,
  ON_COLLECTION_CONFLICT,
  recoveryCommands,
} from "../queries";
import type { BacklogSelection } from "./useBacklogSelection";

type Mode = "retry" | "reprocess";
type FromStage = (typeof REPROCESS_STAGES)[number];

const FROM_OPTIONS: readonly [FromStage, string][] = [
  ["download", "Download"],
  ["archive", "Archive"],
  ["transcribe", "Transcribe"],
  ["speakers", "Speakers"],
];

const LATER_STAGE_HINT =
  "Reuses the archived copy; paid processing is estimated in the preview and stops while optional work is suspended by the budget.";

const FROM_HINT: Record<FromStage, string> = {
  download: "Downloads the original again and runs every later stage.",
  archive: "Reuses the archived copy.",
  transcribe: LATER_STAGE_HINT,
  speakers: LATER_STAGE_HINT,
};

function Estimates({ cells }: { cells: [string, ReactNode][] }) {
  return (
    <dl className="m-0 mt-2 grid grid-cols-4 gap-px overflow-hidden rounded-lg border border-divider bg-divider text-ui max-[640px]:grid-cols-2">
      {cells.map(([label, value]) => (
        <div key={label} className="min-w-0 bg-surface px-2.5 py-1.5">
          <dt className="text-sm text-muted">{label}</dt>
          <dd className="m-0 truncate font-strong text-fg">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function RecoverySection({
  detail,
  onRetry,
  onStop,
  onReprocess,
}: {
  detail: ItemDetail;
  onRetry: () => void;
  onStop: () => void;
  onReprocess: () => void;
}) {
  const stage = detail.item.status.stage_name;
  return (
    <section aria-labelledby="recovery-title" className="rounded-xl border border-divider p-3">
      <h3 id="recovery-title" className="m-0 mb-1 text-md font-semibold tracking-wide text-muted uppercase">
        Recovery
      </h3>
      <p className="m-0 mb-2 text-muted">
        {detail.stopped
          ? "Retrying was stopped, so it isn’t listed as a task. It stays Failed until someone retries or reprocesses it."
          : "No automatic attempts left. A manual retry runs once with the usual error handling and counts against budgets."}
      </p>
      <span className="flex flex-wrap gap-1.5">
        <Button
          size="sm"
          disabledReason={detail.actions.retry.enabled ? null : detail.actions.retry.reason}
          onClick={onRetry}
        >
          <RotateCw aria-hidden="true" />
          Retry {stage}…
        </Button>
        <Button
          size="sm"
          disabledReason={detail.actions.stop.enabled ? null : detail.actions.stop.reason}
          onClick={onStop}
        >
          Stop retrying…
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabledReason={detail.actions.reprocess.enabled ? null : detail.actions.reprocess.reason}
          onClick={onReprocess}
        >
          Reprocess from a stage…
        </Button>
      </span>
    </section>
  );
}

export function RetryDialog({ detail, onClose }: { detail: ItemDetail; onClose: () => void }) {
  const toast = useToast();
  const csrf = useCsrf();
  const preview = useQuery(recoveryPreviewQuery({ action: "retry", item_ids: [detail.item.id] }, csrf()));
  const included = preview.data?.included[0];
  const stage = detail.item.status.stage_name;
  const mutation = useCommandMutation({
    run: (token, reason: string) =>
      recoveryCommands.retry(detail.item.id, { reason, base_revision: detail.item.revision }, token),
    invalidate: AFTER_COLLECTION_CHANGE,
    invalidateOnConflict: ON_COLLECTION_CONFLICT,
    onSuccess: () => {
      toast.show(`Retry queued: ${detail.item.title}`);
      onClose();
    },
  });
  const serverReason = isApiError(mutation.error) ? mutation.error.fieldErrors()["reason"] : undefined;
  const reason = useReason(serverReason);
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Retry ${stage}?`}
      onSubmit={() => {
        if (reason.submit()) mutation.mutate(reason.trimmed);
      }}
      footer={
        <DialogFooter
          onClose={onClose}
          pending={mutation.isPending}
          disabledReason={preview.data && !included ? (preview.data.not_included[0]?.why ?? "Nothing to retry") : null}
          confirm={
            <>
              <RotateCw aria-hidden="true" />
              {mutation.isError ? "Try again" : `Retry ${stage}`}
            </>
          }
        />
      }
    >
      <DialogText>
        <b>{detail.item.title}</b>
      </DialogText>
      {detail.item.status.reason ? <DialogText>{detail.item.status.reason}.</DialogText> : null}
      <Estimates
        cells={[
          ["Stage", `${included?.stage_name ?? stage} and later`],
          ["Attempts", "1 manual attempt"],
          ["Estimated cost", included ? formatCost(included.estimated_cost.amount_decimal) : "…"],
          [
            "Route",
            <span key="r" title={included?.route}>
              {included?.route ?? "…"}
            </span>,
          ],
        ]}
      />
      <p className="m-0 mt-2 text-md text-muted">
        Automatic attempts aren’t reset.
        {preview.data?.warnings.map((w) => ` ${w}.`).join("")}
      </p>
      <ReasonField
        value={reason.reason}
        onChange={reason.setReason}
        error={reason.error}
        placeholder="e.g. The anti-bot check is lifted since 14:00"
      />
      {serverReason ? null : <FailedCallout error={mutation.error ?? preview.error} />}
    </Dialog>
  );
}

export function StopDialog({ detail, onClose }: { detail: ItemDetail; onClose: () => void }) {
  return (
    <ReasonDialog
      title="Stop retrying?"
      confirm="Stop retrying"
      success="Retrying stopped. The item stays Failed."
      onClose={onClose}
      run={(reason, csrf) =>
        recoveryCommands.stop(detail.item.id, { reason, base_revision: detail.item.revision }, csrf)
      }
      invalidate={AFTER_COLLECTION_CHANGE}
      invalidateOnConflict={ON_COLLECTION_CONFLICT}
      placeholder="e.g. Paywalled; the report is secondary and the original stays linked"
      extra={
        <>
          <DialogText>
            <b>{detail.item.title}</b>
          </DialogText>
          <DialogText>
            The item stays <b>Failed</b> with its reason, and it’s no longer listed as an open task. Nothing retries it
            unless someone retries or reprocesses it later.
          </DialogText>
        </>
      }
    />
  );
}

function PreviewList({ title, children, count }: { title: string; count: number; children: ReactNode }) {
  return (
    <div className="mt-3">
      <h4 className="m-0 mb-1 text-sm font-semibold text-muted uppercase">
        {title} <span className="font-mono font-normal">{count}</span>
      </h4>
      <ul
        tabIndex={0}
        aria-label={`${title} items`}
        className="m-0 max-h-[160px] list-none divide-y divide-divider overflow-auto rounded-lg border border-divider p-0 text-ui"
      >
        {children}
      </ul>
    </div>
  );
}

function PreviewBody({ preview }: { preview: RecoveryPreview }) {
  return (
    <div aria-live="polite">
      <Estimates
        cells={[
          ["Items", String(preview.included.length)],
          ["Attempts", "1 manual each"],
          ["Estimated cost", formatCost(preview.estimated_cost.amount_decimal)],
          [
            preview.routes.length > 1 ? "Routes" : "Route",
            <span key="r" title={preview.routes.join(", ")}>
              {preview.routes.length > 1 ? `${String(preview.routes.length)} routes` : (preview.routes[0] ?? "—")}
            </span>,
          ],
        ]}
      />
      <p className="m-0 mt-2 text-md text-muted">{preview.notes.join(". ")}.</p>
      {preview.included.length > 0 ? (
        <PreviewList title="Included" count={preview.included.length}>
          {preview.included.map((i) => (
            <li key={i.id} className="flex items-baseline gap-2 px-2.5 py-1">
              <span className="text-done" aria-hidden="true">
                +
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-fg" title={i.title}>
                  {i.title}
                </span>
                <small className="text-sm text-muted">
                  {i.stage_name} · {i.route}
                </small>
              </span>
              <span className="font-mono text-xs text-muted">{formatCost(i.estimated_cost.amount_decimal)}</span>
            </li>
          ))}
        </PreviewList>
      ) : null}
      {preview.not_included.length > 0 ? (
        <PreviewList title="Not included" count={preview.not_included.length}>
          {preview.not_included.map((i) => (
            <li key={i.id} className="flex items-baseline gap-2 px-2.5 py-1">
              <span className="text-muted" aria-hidden="true">
                ·
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-fg" title={i.title}>
                  {i.title}
                </span>
                <small className="text-sm text-muted">{i.why}</small>
              </span>
            </li>
          ))}
        </PreviewList>
      ) : null}
      {preview.warnings.length > 0 ? (
        <Callout tone="waiting" className="mt-3">
          {preview.warnings.map((w) => (
            <span key={w} className="block">
              {w}.
            </span>
          ))}
        </Callout>
      ) : null}
      {preview.included.length === 0 ? (
        <Callout tone="attention" role="alert" className="mt-3">
          Nothing to {preview.action === "retry" ? "retry" : "reprocess"} with these settings.
          {preview.action === "retry" ? " Try Reprocess from a stage." : ""}
        </Callout>
      ) : null}
    </div>
  );
}

function scopeText(ids: readonly string[], matching: number | null | undefined, filtered: boolean): string {
  const rest = "Only items where the action makes sense are included; the rest are listed with the reason.";
  if (filtered)
    return matching === null || matching === undefined
      ? "Counting the items that match these filters…"
      : `${String(matching)} ${matching === 1 ? "item matches" : "items match"} these filters. ${rest} Items that arrive after this preview aren’t included.`;
  return ids.length === 1
    ? "Only an item where the action makes sense is included."
    : `${String(ids.length)} selected. ${rest}`;
}

function useRecoveryPreview(
  { ids, filter, mode, from }: { ids: string[]; filter: ItemsFilter | undefined; mode: Mode; from: FromStage },
  csrf: string | null,
) {
  const choice = { action: mode, ...(mode === "reprocess" ? { from_stage: from } : {}) };
  const request = filter ? { ...choice, filter } : { ...choice, item_ids: ids };
  const preview = useQuery({ ...recoveryPreviewQuery(request, csrf), placeholderData: (previous) => previous });
  const data = preview.data;
  const shown = data ? [...data.included, ...data.not_included].map((i) => i.id) : [];
  return {
    request,
    preview,
    count: data?.included.length ?? 0,
    commandIds: filter ? shown : ids,
    // DI-120: a filtered command takes the set its current preview showed, so it waits for that preview.
    waiting: filter !== undefined && (preview.isPlaceholderData || preview.isError || !data),
  };
}

export function RecoveryDialog({
  ids,
  filter,
  title,
  initialMode,
  onClose,
  onQueued,
}: {
  ids: string[];
  /** DI-120: all items matching the backlog's filters instead of `ids`; the command takes the set the preview showed */
  filter?: ItemsFilter | undefined;
  title: ReactNode;
  initialMode: Mode;
  onClose: () => void;
  onQueued: () => void;
}) {
  const toast = useToast();
  const csrf = useCsrf();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [from, setFrom] = useState<FromStage>("download");
  const { request, preview, count, commandIds, waiting } = useRecoveryPreview({ ids, filter, mode, from }, csrf());
  const mutation = useCommandMutation({
    run: (token, reason: string) => recoveryCommands.recover({ ...request, item_ids: commandIds, reason }, token),
    invalidate: AFTER_COLLECTION_CHANGE,
    invalidateOnConflict: ON_COLLECTION_CONFLICT,
    onSuccess: (result) => {
      toast.show(`${String(result.included)} ${result.included === 1 ? "item" : "items"} queued`);
      onQueued();
    },
  });
  const serverReason = isApiError(mutation.error) ? mutation.error.fieldErrors()["reason"] : undefined;
  const reason = useReason(serverReason);
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={title}
      onSubmit={() => {
        if (reason.submit()) mutation.mutate(reason.trimmed);
      }}
      footer={
        <DialogFooter
          onClose={onClose}
          pending={mutation.isPending}
          disabled={waiting}
          disabledReason={preview.data && count === 0 ? "No item qualifies" : null}
          confirm={
            <>
              <RotateCw aria-hidden="true" />
              {mutation.isError ? "Try again" : `Queue ${String(count)} ${count === 1 ? "item" : "items"}`}
            </>
          }
        />
      }
    >
      <DialogText>{scopeText(ids, preview.data?.matching, filter !== undefined)}</DialogText>
      <RadioCards
        label="What to do"
        value={mode}
        onChange={setMode}
        options={[
          {
            value: "retry",
            label: "Retry the failed stage",
            description: "Failed items only; each gets one manual attempt",
          },
          {
            value: "reprocess",
            label: "Reprocess from a stage",
            description: "Runs the chosen stage and every later stage again",
          },
        ]}
      />
      {mode === "reprocess" ? (
        <div className="mt-3">
          <FieldLabel htmlFor="recovery-from">Reprocess from</FieldLabel>
          <Select
            id="recovery-from"
            className="max-w-[280px]"
            value={from}
            onChange={(e) => {
              setFrom(REPROCESS_STAGES.find((st) => st === e.target.value) ?? "download");
            }}
          >
            {FROM_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <p className="m-0 mt-1 text-sm text-muted">{FROM_HINT[from]}</p>
        </div>
      ) : null}
      {preview.data ? (
        <PreviewBody preview={preview.data} />
      ) : (
        <p className="m-0 mt-3 text-muted">Preparing the preview…</p>
      )}
      <ReasonField
        value={reason.reason}
        onChange={reason.setReason}
        error={reason.error}
        placeholder="e.g. The anti-bot check is lifted since 14:00"
      />
      {serverReason ? null : <FailedCallout error={mutation.error ?? preview.error} />}
    </Dialog>
  );
}

function MatchingToggle({
  selection,
  loaded,
  matching,
}: {
  selection: BacklogSelection;
  loaded: number;
  matching: number;
}) {
  if (selection.allMatching)
    return (
      <LinkButton onClick={selection.selectLoaded}>
        Select only the {loaded === 1 ? "loaded row" : `${String(loaded)} loaded rows`}
      </LinkButton>
    );
  if (!selection.allLoaded || matching <= loaded) return null;
  return <LinkButton onClick={selection.selectAllMatching}>Select all {matching} matching</LinkButton>;
}

export function BulkBar({
  selection,
  rows,
  matching,
  failed,
  onOpen,
  canRecover,
}: {
  selection: BacklogSelection;
  rows: readonly ItemRow[];
  /** The server's count for the current filters */
  matching: number;
  failed: number;
  onOpen: () => void;
  canRecover: string | null;
}) {
  const { picked, allMatching } = selection;
  if (picked.size === 0 && !allMatching) return null;
  const shown = new Set<string>(rows.map((r) => r.id));
  const hidden = allMatching ? 0 : [...picked].filter((id) => !shown.has(id)).length;
  return (
    <div
      role="region"
      aria-label="Selection"
      className="mx-4 mb-2 flex flex-wrap items-center gap-2.5 rounded-xl border border-link/35 bg-accent-soft py-1.5 pr-2 pl-3 text-md max-compact:mx-2"
    >
      <span>
        <b className="font-semibold">
          {allMatching ? `All ${String(matching)} matching selected` : `${String(picked.size)} selected`}
        </b>
        <span className="text-muted">
          {" "}
          · {failed} failed{hidden > 0 ? ` · ${String(hidden)} hidden by filters` : ""}
        </span>
      </span>
      <MatchingToggle selection={selection} loaded={rows.length} matching={matching} />
      <span className="ml-auto flex flex-wrap items-center gap-1.5">
        <Button size="sm" variant="primary" disabledReason={canRecover} onClick={onOpen}>
          <RotateCw aria-hidden="true" />
          Retry / Reprocess selected…
        </Button>
        <Button size="sm" variant="ghost" onClick={selection.clear}>
          Clear selection
        </Button>
      </span>
    </div>
  );
}

function CancelJobDialog({ job, onClose }: { job: BulkJobView; onClose: () => void }) {
  const toast = useToast();
  const mutation = useCommandMutation({
    run: (token, reason: string) => recoveryCommands.cancelJob(job.id, { reason }, token),
    invalidate: AFTER_COLLECTION_CHANGE,
    invalidateOnConflict: ON_COLLECTION_CONFLICT,
    onSuccess: (result) => {
      toast.show(
        `Cancelled. ${String(result.started)} started ${result.started === 1 ? "item keeps" : "items keep"} running.`,
      );
      onClose();
    },
  });
  const serverReason = isApiError(mutation.error) ? mutation.error.fieldErrors()["reason"] : undefined;
  const reason = useReason(serverReason);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Cancel remaining items?"
      onSubmit={() => {
        if (reason.submit()) mutation.mutate(reason.trimmed);
      }}
      footer={
        <DialogFooter onClose={onClose} cancel="Keep running" confirm="Cancel remaining" pending={mutation.isPending} />
      }
    >
      <DialogText>
        <b>{job.label}</b>: {job.total - job.started} of {job.total} items haven’t started and won’t. Items already
        started keep running.
      </DialogText>
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      {serverReason ? null : <FailedCallout error={mutation.error} />}
    </Dialog>
  );
}

function jobHead(job: BulkJobView): string {
  if (job.state === "cancelled") return `${job.label} · cancelled after ${String(job.started)} of ${String(job.total)}`;
  if (job.state === "completed") return `${job.label} · all ${String(job.total)} started`;
  return `${job.label} · ${String(job.started)} of ${String(job.total)} started`;
}

function jobBarClass(job: BulkJobView): string {
  if (job.state === "cancelled") return "bg-muted";
  return job.state === "completed" ? "bg-done" : "bg-link";
}

function JobBar({ job, onCancel }: { job: BulkJobView; onCancel: () => void }) {
  const toast = useToast();
  const dismiss = useCommandMutation({
    run: (token) => recoveryCommands.dismissJob(job.id, token),
    invalidate: AFTER_COLLECTION_CHANGE,
    invalidateOnConflict: ON_COLLECTION_CONFLICT,
    onSuccess: () => {
      toast.show("Job dismissed");
    },
  });
  const finished = job.state === "completed" || job.state === "cancelled";
  const pct = Math.round((job.started / job.total) * 100);
  const head = jobHead(job);
  return (
    <div
      role="status"
      className="mx-4 mb-2 flex flex-wrap items-center gap-2.5 rounded-xl border border-border bg-bg py-1.5 pr-2 pl-3 text-md max-compact:mx-2"
    >
      <span className="flex min-w-0 flex-[1_1_260px] flex-col">
        <b className="font-semibold">{head}</b>
        <small className="truncate text-sm text-muted">
          Queued by {job.created_by} · est. {formatCost(job.estimated_cost.amount_decimal)} · “{job.reason}”
        </small>
      </span>
      <span className="h-2 min-w-[100px] flex-[0_1_180px] overflow-hidden rounded-full bg-surface-3" aria-hidden="true">
        <span className={cx("block h-full rounded-full", jobBarClass(job))} style={{ width: `${String(pct)}%` }} />
      </span>
      <span className="w-9 text-right font-mono text-xs text-muted">{pct}%</span>
      {finished ? (
        <Button
          size="xs"
          variant="ghost"
          disabledReason={job.actions.dismiss.enabled ? null : job.actions.dismiss.reason}
          onClick={() => {
            dismiss.mutate();
          }}
        >
          Dismiss
        </Button>
      ) : (
        <Button
          size="xs"
          disabledReason={job.actions.cancel.enabled ? null : job.actions.cancel.reason}
          onClick={onCancel}
        >
          Cancel remaining…
        </Button>
      )}
    </div>
  );
}

export function JobBars() {
  const query = useQuery(jobsQuery);
  const [cancelling, setCancelling] = useState<BulkJobView | null>(null);
  const jobs = query.data?.jobs ?? [];
  if (jobs.length === 0) return null;
  return (
    <div aria-label="Queued work">
      {jobs.map((job) => (
        <JobBar
          key={job.id}
          job={job}
          onCancel={() => {
            setCancelling(job);
          }}
        />
      ))}
      {cancelling ? (
        <CancelJobDialog
          job={cancelling}
          onClose={() => {
            setCancelling(null);
          }}
        />
      ) : null}
    </div>
  );
}
